import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@rapid/contexts/AuthContext";
import { useClearWorkflowQueries } from "@rapid/queries/workflowQueries";
import type { RapidWorkflowDataType } from "@rapid/components/flow/shared/Deploy/documentation/types";
import { WorkflowContext } from "@rapid/contexts/WorkflowContext";

export function WorkflowProvider({ children }: { children: React.ReactNode }) {
    const { user } = useAuth();
    const clearWorkflowQueries = useClearWorkflowQueries();
    const [currentWorkflow, setCurrentWorkflowState] = useState<RapidWorkflowDataType | null>(null);

    // Logout cleanup: when user transitions non-null → null, clear workflow state
    const prevUserRef = useRef(user);
    useEffect(() => {
        if (prevUserRef.current && !user) {
            setCurrentWorkflowState(null);
            clearWorkflowQueries();
        }
        prevUserRef.current = user;
    }, [user, clearWorkflowQueries]);

    const setCurrentWorkflow = useCallback((workflow: RapidWorkflowDataType | null) => {
        setCurrentWorkflowState(workflow);
    }, []);

    const value = useMemo(
        () => ({ currentWorkflow, setCurrentWorkflow }),
        [currentWorkflow, setCurrentWorkflow]
    );

    return <WorkflowContext.Provider value={value}>{children}</WorkflowContext.Provider>;
}
